import { Button, Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ibsys/kit';
import { FC, useMemo } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CONVERSATION_AUDIO_FILE_TYPES, CONVERSATION_AUDIO_MAX_SIZE_MB } from '@/entities/conversations';
import { Dropzone } from '@/shared/ui';
import { RECORD_MAX_FILES } from '../model/constants';
import { buildRecordValues, type RecordValueSetForm } from '../model/recordValueSet';
import { useRecordProperties } from '../model/useRecordProperties';
import { useRecordsUpload } from '../model/useRecordsUpload';
import { RecordFilesList } from './RecordFilesList';
import { RecordValueSet } from './RecordValueSet';

type Props = {
  /** Отмеченные записи списка: пока они есть, блок проставляет им значения вместо загрузки файлов */
  pickedIds: number[];
  /** Снятие отметок после проставления значений */
  onApplied: () => void;
  /** Перезапрос показанных строк списка */
  onRecordsChanged: () => void;
};

/**
 * Блок загрузки записей: набор значений, область файлов и действие — добавить записи по файлам
 * либо, при отмеченных записях, проставить им значения набора.
 */
export const RecordsUploadBlock: FC<Props> = ({ pickedIds, onApplied, onRecordsChanged }) => {
  const { t } = useTranslation(['records', 'common']);
  const methods = useForm<RecordValueSetForm>({ defaultValues: { properties: [] } });
  const entries = useWatch({ control: methods.control, name: 'properties' });
  const { options, propertyLabels, attributesByPath, timeDisplayFormat } = useRecordProperties(entries);
  const { files, addFiles, removeFile, reportRejectedFiles, addRecords, applyToRecords, isSubmitting } =
    useRecordsUpload({ pickedIds, onApplied, onRecordsChanged });

  const values = useMemo(
    () => buildRecordValues(entries, attributesByPath, timeDisplayFormat),
    [entries, attributesByPath, timeDisplayFormat],
  );

  const isPicking = pickedIds.length > 0;
  const canAdd = values !== null && files.length > 0 && !isSubmitting;
  const canApply = values !== null && entries.length > 0 && !isSubmitting;

  const handleAdd = () => {
    if (values) void addRecords(values);
  };

  const handleApply = () => {
    if (values) applyToRecords(values);
  };

  return (
    <FormProvider {...methods}>
      <Card>
        <CardHeader>
          <CardTitle>{t('records:upload.title')}</CardTitle>
        </CardHeader>
        {/* Отступ до кнопки задаёт подвал — свой нижний отступ содержимого удвоил бы его */}
        <CardContent className="flex flex-col gap-4 pb-0">
          <RecordValueSet
            options={options}
            propertyLabels={propertyLabels}
            attributesByPath={attributesByPath}
            timeDisplayFormat={timeDisplayFormat}
            disabled={isSubmitting}
          />
          {!isPicking && (
            <>
              <Dropzone
                acceptedFileTypes={CONVERSATION_AUDIO_FILE_TYPES}
                maxFileSize={CONVERSATION_AUDIO_MAX_SIZE_MB}
                maxFiles={RECORD_MAX_FILES}
                currentFilesCount={files.length}
                onFilesChange={addFiles}
                onErrorsChange={reportRejectedFiles}
                disabled={isSubmitting}
                size="compact"
              />
              <RecordFilesList files={files} onRemove={removeFile} disabled={isSubmitting} />
            </>
          )}
        </CardContent>
        <CardFooter>
          {isPicking ? (
            <Button onClick={handleApply} disabled={!canApply} isLoading={isSubmitting}>
              {t('records:upload.applyToRecords')}
            </Button>
          ) : (
            <Button onClick={handleAdd} disabled={!canAdd} isLoading={isSubmitting}>
              {t('common:add')}
            </Button>
          )}
        </CardFooter>
      </Card>
    </FormProvider>
  );
};
